import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import { StatStrip, type StatStripItem } from "../../../../components/UIToolkit/molecules/StatStrip";
import type { RecurringCalculationHeadline } from "../../../../services/backend.service";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { formatIsoDate, NOT_SET } from "./recurringDisplay";

type Props = {
	headline: RecurringCalculationHeadline;
	minimumScore: number;
};

/**
 * The numbers above the table (design §3c "The headline").
 *
 * Accuracy leads because it is the one figure that says whether the whole run is worth approving:
 * it is the mean score measured on held-out weeks the fit never saw, so it is a genuine out-of-sample
 * estimate rather than a restatement of how well the fit memorised its own input.
 */
export const RecurringResultSummary: FC<Props> = ({ headline, minimumScore }) => {
	const items: StatStripItem[] = [
		{
			label: "Accuracy (held out)",
			value: headline.accuracy === null ? NOT_SET : headline.accuracy.toFixed(2),
			emphasis: true,
			dataTestId: "recurring-headline-accuracy",
		},
		{ label: "Employees", value: headline.employees, dataTestId: "recurring-headline-employees" },
		{ label: "Ready", value: headline.ready, dataTestId: "recurring-headline-ready" },
		{ label: "Needs review", value: headline.needsReview, dataTestId: "recurring-headline-needs-review" },
		{ label: "Skipped", value: headline.skipped, dataTestId: "recurring-headline-skipped" },
	];

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="recurring-result-summary">
			<StatStrip items={items} variant="band" dataTestId="recurring-headline-strip" />
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>
				History read from <strong>{formatIsoDate(headline.windowStart)}</strong> to <strong>{formatIsoDate(headline.windowEnd)}</strong>
				{headline.holdoutDays !== null && <> · scored against the last {headline.holdoutDays} days, which the fit never saw</>}
				{headline.basedOn && <> · mostly from {headline.basedOn}</>}
			</Typography>
			{headline.accuracy !== null && headline.accuracy < minimumScore && (
				<InlineHint tone="warning" dataTestId="recurring-headline-low-accuracy">
					The average proposal scores below your minimum of {minimumScore.toFixed(2)}. That usually means the history is too short, too irregular, or that the
					shift hours below are wrong — worth checking before approving anything.
				</InlineHint>
			)}
		</Box>
	);
};
